import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Subscription } from "rxjs";
import { map, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Quote } from "src/model/class/general classes/quote";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { LoggerService } from "./logger.service";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { currentBuildingUnsetAction } from "src/components/shared-components/state/actions/building.action";
import { EventService } from "./event.service";
import { Quotes } from "html2canvas/dist/types/css/property-descriptors/quotes";

@Injectable({
  providedIn: "root",
})
export class DpQuotesService implements OnDestroy, iEvent {
  private _idArea: number = null;
  private _logContext: string = "DpQuotesService";
  private _listQuotes: Array<Quote> = new Array();
  public quotesSubject = new BehaviorSubject<Array<Quote>>(this._listQuotes);
  // Array of subscriptions to unsubscribe when the component is destroyed
  //This must be defined this way since it's coming from the iEvent interface
  _eventSubscriptions: Array<Subscription> = [];

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _loggerService: LoggerService,
    private _eventService: EventService
  ) {
    this.subscribeEvents();
  }

  ngOnDestroy(): void {
    this.unsubscribeEvents();
  }

  /**
   *  Create a new quote
   * @param newQuote  The new quote to create
   * @returns
   */
  public createQuote(newQuote: Quote): Promise<Quote> {
    return this._http
      .post<Quote>(
        environment.httpDPAPIRoot +
        environment.anaciPlatformQuote +
        AppSettings.API_URL_QUOTE,
        newQuote
      )

      .toPromise();
  }

  /**
   * Step 1 of the direct-to-S3 upload: ask the backend for a short-lived
   * presigned PUT URL. No file bytes travel through our API server and no AWS
   * credential ever reaches the client.
   */
  public getUploadUrl(idQuote: number, namefile: string): Promise<string> {
    return this._http
      .get<{ url: string }>(
        environment.httpDPAPIRoot +
        environment.anaciPlatformQuote +
        AppSettings.API_URL_QUOTE +
        "/uploadfile/" +
        idQuote +
        "/url?namefile=" +
        encodeURIComponent(namefile)
      )
      .pipe(
        map((response) => response?.url),
        take(1)
      )
      .toPromise();
  }

  /**
   * Step 2 of the direct-to-S3 upload: PUT the raw file straight to S3 over
   * HTTPS using the presigned URL.
   *
   * This intentionally uses the native fetch() API instead of Angular's
   * HttpClient: the AuthenticationInterceptor attaches an `Authorization:
   * Bearer ...` header (and `Content-Type: application/json`) to every
   * HttpClient request, which would break the SigV4-signed S3 request and leak
   * the session token to S3. fetch() bypasses the interceptor entirely.
   */
  public async uploadFileToPresignedUrl(
    url: string,
    file: File
  ): Promise<void> {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`);
    }
  }

  /**
   *  Get all quotes from the server
   * @returns Promise<Array<Quotes>>
   */
  public getAllQuotes(): Promise<Array<Quote> | number> {
    if (!this._idArea) return Promise.resolve(404);

    const queryString = `?idArea=${this._idArea}`;
    return this._http
      .get<Promise<Array<Quote>>>(
        environment.httpDPAPIRoot +
        environment.anaciPlatformQuote +
        AppSettings.API_URL_QUOTE +
        queryString,
        {
          observe: "response",
        }
      )
      .pipe(
        tap((responseData: HttpResponse<Object>) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "getAllQuotes",
            "DpQuotesService",
            " responseData: " + JSON.stringify(responseData)
          );
        }),
        map((responseData: HttpResponse<Object>) => {
          if (responseData.status !== 200) return responseData.status;
          this.quotesSubject.next(responseData.body as Array<Quote>);
          return responseData.body as Array<Quote>;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   *  Handle the updating of the selected quote
   * @param idSelectedQuotes
   * @param updatedQuote
   * @returns
   */
  public updateSelectedQuote(
    idSelectedQuotes: number,
    updatedQuote: Quote
  ): Promise<Quote> {
    return this._http
      .patch<Quote>(
        environment.httpDPAPIRoot +
        environment.anaciPlatformQuote +
        AppSettings.API_URL_QUOTE +
        "/" +
        idSelectedQuotes,
        updatedQuote
      )
      .toPromise();
  }

  /**
   *  Handle the deleting of file request
   * @param idQuote
   * @returns
   */
  public deleteQuoteFile(idQuote: number): Promise<boolean> {
    return this._http
      .delete<number>(
        environment.httpDPAPIRoot +
        environment.anaciPlatformQuote +
        AppSettings.API_URL_QUOTE +
        "/file" +
        "/" +
        idQuote,
        {
          observe: "response",
        }
      )
      .pipe(
        map((responseData) => {
          return responseData.ok;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   *  Handle the delete quote request
   * @param idQuote id of the quote to delete
   * @returns
   */
  public async deleteQuote(idQuote: number): Promise<HttpResponse<Object>> {
    return this._http
      .delete<number>(
        environment.httpDPAPIRoot +
        environment.anaciPlatformQuote +
        AppSettings.API_URL_QUOTE +
        "/" +
        idQuote,
        {
          observe: "response",
        }
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => {
          return responseData;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   *  Get an uploaded file for a selected quote
   * @param quoteId  selected quote id
   * @returns
   */
  public getUploadedFile(quoteId: number) {
    return this._http
      .get(
        environment.httpDPAPIRoot +
        environment.anaciPlatformQuote +
        AppSettings.API_URL_QUOTE +
        "/file" +
        "/" +
        quoteId
      )
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   * Get the current idArea from the store
   */
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._store
        .select(selectedBuilding)
        .pipe(
          map((building: Building) => {
            return building?.idAreaLocal;
          }),
          tap((idArea) => {
            this._idArea = idArea;
          })
        )
        .subscribe()
    );

    this._eventSubscriptions.push(
      this._eventService?.OnMakeLogout.subscribe(() => {
        this.deselectCurrentBuilding();
      })
    );
  }

  /**
   * Unsubscribe to event-service observable
   */
  public unsubscribeEvents(): void {
    this._eventSubscriptions.map((subscription) => subscription?.unsubscribe());
  }

  /**
   * Unset the current building
   */
  private deselectCurrentBuilding(): void {
    this._store.dispatch(currentBuildingUnsetAction());
  }
}
